import React from "react";
import FontIcon from "@odl/core/components/DataDisplay/FontIcon";
import AddressLookup from "components/AddressInput/AddressLookup";
import FlexBox from "components/FlexBox/FlexBox";
import FormikSelect from "components/FormikSelect/FormikSelect";
import FormikTextInput from "components/FormikTextInput/FormikTextInput";
import { css } from "@emotion/react";
import { Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useFormikContext } from "formik";
import { IExtendedAddressEntity } from "models/Address.model";
import { useTranslation } from "react-i18next";
import { ApplicationSearchFormData } from "models/Application.model";
import { useAuthoritySelectItems } from "./functions/useAuthoritySelectItems";

type Props = {};

const ApplicationSearchFilters: React.FC<Props> = () => {
  const { t } = useTranslation();

  const authoritySelectItems = useAuthoritySelectItems();
  const [address, _setAddress] = React.useState<IExtendedAddressEntity | null>(null);
  const { setFieldValue, submitForm, values, touched, errors, resetForm } = useFormikContext<
    ApplicationSearchFormData
  >();

  const isAdvancedSearch = values.isAdvancedSearch;
  const hasAdvancedSearchError =
    (touched.applicationNumber && Boolean(errors.applicationNumber)) ||
    (touched.authorities && Boolean(errors.authorities));

  const setAddress = React.useCallback(
    async (address: IExtendedAddressEntity | null) => {
      _setAddress(address);
      setFieldValue("address", address?.fullAddress || "");

      if (address) {
        // Automatically submit form when address changed
        await submitForm();
      }
    },
    [submitForm, setFieldValue]
  );

  const handleTriggerAdvancedSearch = React.useCallback(
    (event) => {
      event.preventDefault();

      // Reset address and form
      setAddress(null);
      resetForm();

      setFieldValue("isAdvancedSearch", !isAdvancedSearch);
    },
    [isAdvancedSearch, setFieldValue, resetForm, setAddress]
  );

  if (isAdvancedSearch) {
    return (
      <FlexBox
        flexDirection={"column"}
        spacing={4}
        alignItems={"flex-start"}
        data-testid={"AccessRequestAdvancedFilters"}
      >
        <Button
          size={"large"}
          startIcon={<FontIcon name={"i-navigation-arrow-left"} />}
          onClick={handleTriggerAdvancedSearch}
        >
          {t("Back")}
        </Button>

        <FlexBox flexDirection={"row"} spacing={4} width={"100%"} alignItems={"flex-start"}>
          <FlexBox width={"25%"}>
            <FormikTextInput label={t(`Project number`)} fieldName="applicationNumber" required={true} />
          </FlexBox>
          <FormikSelect
            label={t(`Authorities`)}
            fieldName="authorities"
            multiple={true}
            required={true}
            items={authoritySelectItems}
          />

          <StyledSearchButton
            $hasError={hasAdvancedSearchError}
            data-testid={"SubmitButton"}
            variant={"contained"}
            type={"submit"}
          >
            {t("Search")}
          </StyledSearchButton>
        </FlexBox>
      </FlexBox>
    );
  }

  return (
    <FlexBox
      paddingRight={6}
      paddingLeft={6}
      flexDirection={"row"}
      alignItems={"flex-end"}
      spacing={4}
      data-testid={"AccessRequestFilters"}
    >
      <AddressLookup address={address} onAddressChange={setAddress} />

      <StyledButton
        variant={"outlined"}
        onClick={handleTriggerAdvancedSearch}
        data-testid={"AccessRequestAdvancedFiltersButton"}
      >
        <FontIcon name={"i-controls-settings-adjust"} />
      </StyledButton>
    </FlexBox>
  );
};

const StyledSearchButton = styled(Button, { shouldForwardProp: (prop) => prop !== "$hasError" })<{
  $hasError?: boolean;
}>(
  ({ theme, $hasError = false }) => css`
    min-height: ${theme.spacing(11)};
    min-width: 80px;
    align-self: flex-end;
    ${$hasError &&
    css`
      margin-bottom: ${theme.spacing(6)};
    `}
  `
);

const StyledButton = styled(Button)(
  ({ theme }) =>
    css`
      min-width: 52px;
      height: ${theme.spacing(11)};
    `
);

export default ApplicationSearchFilters;
